<template>
    <div class="apply-detail">
      <div v-if="loading" class="loading-container">
        <el-skeleton :rows="10" animated />
      </div>
  
      <div v-else-if="!applyData || !applyData.id" class="empty-state">
        <el-empty description="申请信息不存在" />
      </div>
  
      <div v-else class="detail-content">
        <!-- 申请基本信息 -->
        <el-card class="basic-info-card">
          <div slot="header" class="card-header">
            <span class="title">申请基本信息</span>
            <div class="status-info">
              <el-tag :type="getStatusTagType(applyData)" size="medium">
                {{ applyData.statusText || getStatusText(applyData.status) }}
              </el-tag>
            </div>
          </div>
          
          <el-descriptions :column="3" border class="info-descriptions">
            <el-descriptions-item label="申请ID">{{ applyData.id }}</el-descriptions-item>
            <el-descriptions-item label="申请时间">{{ formatDateTime(applyData.apply_time) }}</el-descriptions-item>
            <el-descriptions-item label="申请人">{{ applyData.apply_user_name || '用户' + applyData.apply_user_id }}</el-descriptions-item>
            
            <el-descriptions-item label="项目名称" :span="3">
              {{ contractInfo?.WorkName || contractInfo?.workName || '-' }}
            </el-descriptions-item>
            
            <el-descriptions-item label="建设单位">{{ contractInfo?.build_company || '-' }}</el-descriptions-item>
            <el-descriptions-item label="承接单位">{{ contractInfo?.SubjectName || contractInfo?.subjectName || '-' }}</el-descriptions-item>
            
            <el-descriptions-item label="申请理由" :span="3">
              <div class="reason-content">
                {{ applyReason || '无' }}
              </div>
            </el-descriptions-item>
            
            <template v-if="applyData.status !== 0">
              <el-descriptions-item label="审批人">用户{{ applyData.review_user_id }}</el-descriptions-item>
              <el-descriptions-item label="审批时间">{{ formatDateTime(applyData.review_time) }}</el-descriptions-item>
              <el-descriptions-item label="审批备注">
                <div class="remark-content">
                  {{ applyData.review_remark || '无' }}
                </div>
              </el-descriptions-item>
            </template>
          </el-descriptions>
        </el-card>
  
        <!-- 修改内容对比 -->
        <el-card class="modify-compare-card">
          <div slot="header" class="card-header">
            <span class="title">修改内容对比</span>
            <el-tag type="info" size="small">
              共修改 {{ fieldComparison.length }} 项
            </el-tag>
          </div>
          
          <div class="compare-table">
            <el-table
              :data="fieldComparison"
              border
              size="small"
              class="compare-content-table"
            >
              <el-table-column prop="fieldName" label="字段" width="150" fixed />
              <el-table-column label="修改前" min-width="200">
                <template slot-scope="scope">
                  <div class="value-cell old-value">
                    <span class="value-text">{{ scope.row.oldValue || '-' }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="修改后" min-width="200">
                <template slot-scope="scope">
                  <div class="value-cell new-value">
                    <span class="value-text">{{ scope.row.newValue || '-' }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="变化" width="100">
                <template slot-scope="scope">
                  <el-tag 
                    size="mini" 
                    :type="scope.row.changed ? 'warning' : 'info'"
                  >
                    {{ scope.row.changed ? '已修改' : '未修改' }}
                  </el-tag>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-card>
  
        <!-- 操作按钮区 -->
        <div class="action-buttons" v-if="canApprove">
          <el-divider content-position="left">操作</el-divider>
          
          <!-- 审批操作（待审批状态） -->
          <div v-if="canApprove" class="approve-actions">
            <el-button 
              type="success" 
              size="medium" 
              @click="handleApprove"
              :loading="actionLoading"
            >
              <i class="el-icon-check"></i> 通过审批
            </el-button>
            
            <el-button 
              type="danger" 
              size="medium" 
              @click="showRejectDialog"
              :loading="actionLoading"
            >
              <i class="el-icon-close"></i> 拒绝申请
            </el-button>
          </div>
          
        </div>
      </div>
  
      <!-- 拒绝申请对话框 -->
      <el-dialog
        title="拒绝申请"
        :visible.sync="rejectDialogVisible"
        width="500px"
        :append-to-body="true"
        :close-on-click-modal="false"
        @closed="resetRejectForm"
      >
        <el-form ref="rejectForm" :model="rejectForm" :rules="rejectRules" label-width="80px">
          <el-form-item label="拒绝理由" prop="reviewRemark" required>
            <el-input
              type="textarea"
              v-model="rejectForm.reviewRemark"
              placeholder="请详细说明拒绝理由，申请人将看到此理由"
              :rows="4"
              maxlength="200"
              show-word-limit
            />
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="rejectDialogVisible = false">取消</el-button>
          <el-button 
            type="danger" 
            @click="handleReject" 
            :loading="actionLoading"
          >
            确认拒绝
          </el-button>
        </div>
      </el-dialog>
  
    </div>
  </template>
  
  <script>
  import { getContractApplyDetail, approveContractApply, rejectContractApply } from '../api';
  
  export default {
    name: 'ApplyDetail',
    
    props: {
      applyId: {
        type: Number,
        required: true
      },
      canApprove: {
        type: Boolean,
        default: false
      }
    },
    
    data() {
      return {
        loading: false,
        actionLoading: false,
        applyData: null,
        contractInfo: null,
        modifyData: null,
        applyReason: '',
        oldData: {},
        newData: {},
        changedFields: [],
        
        // 字段名映射
        fieldNameMap: {
          'WorkName': '项目名称',
          'ReturnId': '归档编号',
          'content': '服务内容',
          'build_company': '建设单位',
          'receive_companyId': '承接单位ID',
          'win_company': '中标单位',
          'DeadLine': '截止时间',
          'FirstPrice': '招标控制价',
          'SecondPrice': '中标价',
          'HtPrice': '合同金额',
          'PayPrice': '已开票金额',
          'PingPrice': '评委费',
          'note': '备注'
        },
        
        // 拒绝对话框
        rejectDialogVisible: false,
        rejectForm: {
          reviewRemark: ''
        },
        rejectRules: {
          reviewRemark: [
            { required: true, message: '请填写拒绝理由', trigger: 'blur' },
            { min: 3, message: '拒绝理由至少3个字符', trigger: 'blur' }
          ]
        },
        
      };
    },
    
    computed: {
      
      // 字段对比数据 - 只显示实际修改的字段
      fieldComparison() {
        // 直接使用changedFields中已经存储的oldValue和newValue
        return this.changedFields
          .map(fieldInfo => {
            const field = fieldInfo.field || fieldInfo;
            // 优先使用changedFields中已经存储的值
            let oldValue = fieldInfo.oldValue;
            let newValue = fieldInfo.newValue;
            
            // 如果changedFields中没有值，尝试从oldData/newData获取
            // 注意：不应该从contractInfo获取，因为合同可能已经被修改过了
            if (oldValue === undefined && this.oldData && this.oldData[field] !== undefined) {
              oldValue = this.oldData[field];
            }
            if (newValue === undefined && this.newData && this.newData[field] !== undefined) {
              newValue = this.newData[field];
            }
            
            // 格式化显示 - 处理空值
            let formattedOldValue = '-';
            let formattedNewValue = '-';
            
            // 处理oldValue - 空字符串也应该显示，只有undefined和null才显示"-"
            if (oldValue !== undefined && oldValue !== null && oldValue !== '') {
              const oldValStr = String(oldValue);
              const trimmedOldVal = oldValStr.trim();
              // 如果trim后为空字符串，显示为空字符串而不是"-"
              if (trimmedOldVal === '') {
                formattedOldValue = '';
              } else {
                // 格式化价格字段
                if (['FirstPrice', 'SecondPrice', 'HtPrice', 'PayPrice', 'PingPrice'].includes(field)) {
                  try {
                    const numVal = Number(trimmedOldVal);
                    if (!isNaN(numVal)) {
                      formattedOldValue = '¥' + numVal.toFixed(2);
                    } else {
                      formattedOldValue = trimmedOldVal;
                    }
                  } catch (e) {
                    formattedOldValue = trimmedOldVal;
                  }
                }
                // 格式化日期字段
                else if (field === 'DeadLine') {
                  formattedOldValue = this.formatDate(trimmedOldVal);
                }
                // 其他字段
                else {
                  formattedOldValue = trimmedOldVal;
                }
              }
            } else if (oldValue === '') {
              // 空字符串也显示为空字符串
              formattedOldValue = '';
            }
            
            // 处理newValue - 恢复之前能正常工作的逻辑
            if (newValue !== undefined && newValue !== null) {
              const newValStr = String(newValue);
              const trimmedNewVal = newValStr.trim();
              // 如果trim后为空字符串，显示为空字符串而不是"-"
              if (trimmedNewVal === '') {
                formattedNewValue = '';
              } else {
                // 格式化价格字段
                if (['FirstPrice', 'SecondPrice', 'HtPrice', 'PayPrice', 'PingPrice'].includes(field)) {
                  try {
                    formattedNewValue = '¥' + Number(trimmedNewVal).toFixed(2);
                  } catch (e) {
                    formattedNewValue = trimmedNewVal;
                  }
                }
                // 格式化日期字段
                else if (field === 'DeadLine') {
                  formattedNewValue = this.formatDate(trimmedNewVal);
                }
                // 其他字段
                else {
                  formattedNewValue = trimmedNewVal;
                }
              }
            }
            
            return {
              field,
              fieldName: this.fieldNameMap[field] || field,
              oldValue: formattedOldValue,
              newValue: formattedNewValue,
              changed: true
            };
          });
      }
    },
    
    watch: {
      applyId: {
        immediate: true,
        handler(newVal) {
          if (newVal) {
            this.loadApplyDetail();
          }
        }
      }
    },
    
    methods: {
      // 加载申请详情
      async loadApplyDetail() {
        if (!this.applyId) {
          return;
        }
        
        this.loading = true;
        try {
          const res = await getContractApplyDetail({
            applyId: this.applyId
          });
          
          if (res.code === 0 && res.data) {
            this.applyData = res.data.apply || {};
            this.contractInfo = res.data.contract || {};
            this.modifyData = res.data.modifyData || {};
            
            
            // 解析修改数据
            // 修改前的数据：从 oldData 读取（申请时保存的原始值）- 修复已审批后显示错误的问题
            // 修改后的数据：从 newData 读取（申请时保存的新值）- 保持之前能正常工作的方式
            if (this.modifyData && typeof this.modifyData === 'object') {
              this.applyReason = res.data.applyReason || this.modifyData.applyReason || '';
              
              // 修改前的数据：从 oldData 读取（申请时保存的原始值）
              this.oldData = res.data.oldData || this.modifyData.oldData || {};
              
              // 修改后的数据：优先从modifyData.newData读取（因为res.data.newData可能是JSON引用）
              // 如果modifyData.newData不存在，再尝试从res.data.newData读取
              if (this.modifyData && this.modifyData.newData && typeof this.modifyData.newData === 'object') {
                this.newData = this.modifyData.newData;
              } else if (res.data.newData && typeof res.data.newData === 'object' && !res.data.newData.$ref) {
                // 只有当res.data.newData不是JSON引用时才使用
                this.newData = res.data.newData;
              } else {
                this.newData = {};
              }
              
              // 优先使用直接返回的changedFields
              const changedFieldNames = res.data.changedFields || this.modifyData.changedFields || [];
              
              // 构建修改字段列表 - 使用之前能正常工作的方式
              this.changedFields = changedFieldNames.map(fieldName => {
                // 确保正确读取值，使用 hasOwnProperty 检查属性是否存在
                const oldVal = this.oldData && this.oldData.hasOwnProperty(fieldName) ? this.oldData[fieldName] : '';
                const newVal = this.newData && this.newData.hasOwnProperty(fieldName) ? this.newData[fieldName] : '';
                return {
                  field: fieldName,
                  fieldName: this.fieldNameMap[fieldName] || fieldName,
                  oldValue: oldVal !== undefined && oldVal !== null ? oldVal : '',  // 从申请时保存的oldData读取（原始值）
                  newValue: newVal !== undefined && newVal !== null ? newVal : ''   // 从申请时保存的newData读取（新值）
                };
              });
            } else {
              // 即使modifyData为空，也尝试使用直接返回的数据
              this.applyReason = res.data.applyReason || '';
              
              // 修改前的数据：从 oldData 读取
              this.oldData = res.data.oldData || {};
              
              // 修改后的数据：优先从modifyData.newData读取（因为res.data.newData可能是JSON引用）
              if (this.modifyData && this.modifyData.newData && typeof this.modifyData.newData === 'object') {
                this.newData = this.modifyData.newData;
              } else if (res.data.newData && typeof res.data.newData === 'object' && !res.data.newData.$ref) {
                this.newData = res.data.newData;
              } else {
                this.newData = {};
              }
              
              const changedFieldNames = res.data.changedFields || [];
              
              this.changedFields = changedFieldNames.map(fieldName => {
                return {
                  field: fieldName,
                  fieldName: this.fieldNameMap[fieldName] || fieldName,
                  oldValue: this.oldData[fieldName],  // 从申请时保存的oldData读取（原始值）
                  newValue: this.newData[fieldName]   // 从申请时保存的newData读取（新值）
                };
              });
            }
          } else {
            this.$message.error(res.msg || '加载申请详情失败');
            this.applyData = null;
          }
        } catch (error) {
          this.$message.error('加载申请详情失败: ' + (error.message || '未知错误'));
          this.applyData = null;
        } finally {
          this.loading = false;
        }
      },
      
      // 获取状态文本
      getStatusText(status) {
        switch (status) {
          case 0: return '待审核';
          case 1: return '已通过';
          case 2: return '已拒绝';
          default: return '未知';
        }
      },
      
      // 获取状态标签类型
      getStatusTagType(apply) {
        const status = apply.status;
        switch (status) {
          case 0: return 'warning';
          case 1: return 'success';
          case 2: return 'danger';
          default: return 'info';
        }
      },
      
      // 格式化日期
      formatDate(date) {
        if (!date) return '-';
        if (typeof date === 'string' && date.length >= 10) {
          return date.substring(0, 10);
        }
        if (date instanceof Date) {
          return date.toISOString().substring(0, 10);
        }
        return '-';
      },
      
      // 格式化日期时间
      formatDateTime(datetime) {
        if (!datetime) return '-';
        const date = new Date(datetime);
        return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
      },
      
      // 处理审批通过（直接生效）
      async handleApprove() {
        try {
          await this.$confirm('确定要通过此申请吗？', '提示', {
            confirmButtonText: '确定通过',
            cancelButtonText: '取消',
            type: 'warning'
          });
          
          this.actionLoading = true;
          
          const res = await approveContractApply({
            applyId: this.applyId,
            reviewRemark: '审批通过'
          });
          
          if (res.code === 0) {
            this.$message.success('审批通过');
            this.$emit('approved');
          } else {
            this.$message.error(res.msg || '审批失败');
          }
        } catch (error) {
          if (error !== 'cancel') {
            this.$message.error('审批失败');
          }
        } finally {
          this.actionLoading = false;
        }
      },
      
      // 显示拒绝对话框
      showRejectDialog() {
        this.rejectDialogVisible = true;
      },
      
      // 处理拒绝
      async handleReject() {
        try {
          await this.$refs.rejectForm.validate();
          
          this.actionLoading = true;
          
          const res = await rejectContractApply({
            applyId: this.applyId,
            reviewRemark: this.rejectForm.reviewRemark
          });
          
          if (res.code === 0) {
            this.$message.success('已拒绝该申请');
            this.rejectDialogVisible = false;
            this.$emit('rejected');
          } else {
            this.$message.error(res.msg || '拒绝失败');
          }
        } catch (error) {
          if (error.name !== 'Error') {
            this.$message.error('操作失败');
          }
        } finally {
          this.actionLoading = false;
        }
      },
      
      
      // 重置拒绝表单
      resetRejectForm() {
        this.rejectForm = {
          reviewRemark: ''
        };
        if (this.$refs.rejectForm) {
          this.$refs.rejectForm.clearValidate();
        }
      }
    }
  };
  </script>
  
  <style scoped>
  .apply-detail {
    padding: 10px;
  }
  
  .loading-container {
    padding: 40px;
    background: white;
    border-radius: 8px;
  }
  
  .empty-state {
    text-align: center;
    padding: 60px 0;
  }
  
  .detail-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  
  .basic-info-card,
  .modify-compare-card {
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  }
  
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }
  
  .status-info {
    display: flex;
    gap: 10px;
  }
  
  .info-descriptions {
    font-size: 13px;
  }
  
  .reason-content,
  .remark-content {
    padding: 5px 0;
    color: #606266;
    line-height: 1.5;
  }
  
  .compare-table {
    background: white;
    border-radius: 4px;
    border: 1px solid #ebeef5;
    overflow: hidden;
  }
  
  .compare-content-table {
    margin: 0;
  }
  
  .value-cell {
    padding: 8px;
    border-radius: 4px;
    min-height: 40px;
    display: flex;
    align-items: center;
  }
  
  .old-value {
    background-color: #fef0f0;
    border-left: 3px solid #f56c6c;
  }
  
  .new-value {
    background-color: #f0f9eb;
    border-left: 3px solid #67c23a;
  }
  
  .value-text {
    word-break: break-word;
  }
  
  .action-buttons {
    margin-top: 20px;
    padding: 20px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  }
  
  .approve-actions {
    display: flex;
    gap: 15px;
    margin-top: 15px;
  }
  
  .dialog-footer {
    text-align: right;
  }
  
  .field-list {
    margin: 0;
    padding-left: 0;
    list-style: none;
  }
  
  .field-list li {
    margin: 10px 0;
    padding: 8px 12px;
    background: white;
    border-radius: 4px;
    border: 1px solid #ebeef5;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .old-value-text {
    color: #f56c6c;
    text-decoration: line-through;
  }
  
  .new-value-text {
    color: #67c23a;
    font-weight: 500;
  }
  
  .field-list .el-icon-right {
    color: #909399;
  }
  
  /* 响应式适配 */
  @media screen and (max-width: 768px) {
    .apply-detail {
      padding: 5px;
    }
    
    .card-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }
    
    .info-descriptions {
      font-size: 12px;
    }
    
    .compare-content-table {
      font-size: 12px;
    }
    
    .value-cell {
      padding: 4px;
      min-height: 30px;
    }
    
    .approve-actions {
      flex-direction: column;
    }
    
    .field-list li {
      flex-direction: column;
      align-items: flex-start;
      gap: 5px;
    }
  }
  </style>